import { useState } from 'react';
import {
  countryDisplayName,
  defaultCountryFromTimeZone,
  isValidCountryCode,
  schoolZoneForResidence,
  suggestCommunes,
  suggestCountries,
  type CountryCode,
} from '@repere/core';
import { ChampAutocomplete, type SuggestionAutocomplete } from '../ui/ChampAutocomplete';
import { useTraduction } from '../i18n/langue';

export interface Residence {
  country: CountryCode | '';
  city: string;
}

/** Une résidence vide n'est pas un pays vide : elle démarre sur le pays le plus probable. */
export function residenceParDefaut(): Residence {
  return { country: defaultCountryFromTimeZone(), city: '' };
}

/**
 * Pays et ville de résidence de l'enfant, saisis pour affiner l'affichage des congés
 * scolaires (zone A/B/C en France) une fois cette information exploitée par le calendrier.
 *
 * **Le pays est pré-rempli, jamais imposé.** Il part du fuseau horaire du navigateur —
 * une détection qui ne demande aucune permission, contrairement à la géolocalisation —
 * mais reste un champ ordinaire : une famille expatriée le corrige comme n'importe quel
 * autre.
 *
 * **La ville reste un texte libre.** L'autocomplétion propose les communes de plus de
 * 2 000 habitants ; en dessous de ce seuil, ou hors de France, rien ne s'affiche et la
 * saisie est acceptée telle quelle — voir `ChampAutocomplete`.
 */
export function ChampResidence({
  residence,
  onChange,
}: {
  residence: Residence;
  onChange: (residence: Residence) => void;
}) {
  const { locale, messages } = useTraduction();

  // Le texte affiché dans le champ pays diverge de sa valeur (un code ISO) : « FR » ne se
  // tape pas, « France » si. Les deux vivent donc séparément, et une saisie qui ne
  // correspond plus au pays choisi efface simplement ce dernier — un champ affichant
  // « Fra » ne doit pas laisser croire que la France reste sélectionnée.
  const [saisiePays, setSaisiePays] = useState(() =>
    residence.country ? countryDisplayName(residence.country, locale) : '',
  );

  const suggestionsPays: SuggestionAutocomplete[] = suggestCountries(saisiePays, locale).map(
    (pays) => ({ cle: pays.code, libelle: pays.name }),
  );
  const suggestionsVille: SuggestionAutocomplete[] = suggestCommunes(residence.city).map(
    (commune) => ({ cle: commune.inseeCode, libelle: commune.name }),
  );

  const zone = schoolZoneForResidence({ country: residence.country || 'FR', city: residence.city });

  return (
    <div className="champ-residence">
      <ChampAutocomplete
        label={messages.creerDossier.residence.pays}
        valeur={saisiePays}
        onSaisie={(texte) => {
          setSaisiePays(texte);
          // Une saisie qui ne reprend plus le nom du pays choisi ne le désigne plus :
          // le pays retenu doit disparaître avec elle, pas rester silencieusement collé
          // à un texte qui a changé.
          if (residence.country && texte !== countryDisplayName(residence.country, locale)) {
            onChange({ ...residence, country: '' });
          }
        }}
        suggestions={suggestionsPays}
        onChoisir={(suggestion) => {
          if (!isValidCountryCode(suggestion.cle)) return;
          setSaisiePays(suggestion.libelle);
          onChange({ ...residence, country: suggestion.cle });
        }}
      />
      <ChampAutocomplete
        label={messages.creerDossier.residence.ville}
        valeur={residence.city}
        onSaisie={(texte) => onChange({ ...residence, city: texte })}
        suggestions={suggestionsVille}
        onChoisir={(suggestion) => onChange({ ...residence, city: suggestion.libelle })}
        autoComplete="off"
        aide={
          zone
            ? messages.creerDossier.residence.zone(zone)
            : residence.country === 'FR' && residence.city
              ? messages.creerDossier.residence.zoneInconnue
              : undefined
        }
      />
    </div>
  );
}
